import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AutoTextarea from './AutoTextarea';
import { resolveBlockOrder } from '../lib/promoBlockOrder';
import type { PromoContentDTO, PromoContentSection, PromoContentItem, BandFileDTO } from '../api';

/**
 * Shared editor for the PUBLIC promo-kit page content blob — used by Band
 * Settings (band default) and the Tours page (per-tour). Pure controlled
 * editor: it holds NO fetch logic; the parent loads/saves the blob. Everything
 * here is PUBLIC page text, so there is deliberately no sensitive field — and
 * the files it references are only the band's own band-level `promo_kit`
 * files (the server re-validates every id).
 *
 * Sections carry a mixed, ordered `items` list (files / external links /
 * YouTube videos). OPTION B (2026-10-19):
 *  • bio formatting bar — B / I / link / list buttons wrap the textarea
 *    selection with minimal markdown (**bold**, *italic*, [text](https://url),
 *    "- " list prefix); the stored data stays plain markdown, rendered by the
 *    public page's strict inline renderer;
 *  • drag & drop reordering (native HTML5) for items, freely mixed between
 *    types — the ↑/↓ buttons stay as the keyboard/touch fallback (drag & drop
 *    does not work on touch);
 *  • a "Hero image" block: Auto (legacy derivation), a picker over the band's
 *    promo_kit images (`heroImageFileId` — validated band-scoped server-side),
 *    or None (dark gradient only).
 *
 * BLOCK ORDER (spec docs/SPEC-PROMO-BLOCK-ORDER.md, 2026-10-20): the editor
 * column is now a list of MOVABLE content blocks — Intro / Bio / Quotes /
 * each section / Socials / Contact — reordered by drag & drop (same ⠿ handles
 * as the items) with ↑/↓ fallback. The order is persisted as the additive
 * blob key `blockOrder` (ids: intro, bio, quotes, socials, contact,
 * section:<key>); the hero picker stays OUTSIDE the draggable area, fixed at
 * the top. The old "video sections last" rule is gone — the user decides.
 * A file deleted after being referenced is shown as "missing" here and simply
 * dropped on the public page (never a 500). An invalid link/video URL is
 * rejected by the server with a 400 — it is never saved silently; the save
 * failure surfaces the error in the parent.
 */
export default function PromoContentEditor({
  value,
  onChange,
  files,
  showTitle = false,
  disabled = false,
}: {
  value: PromoContentDTO;
  onChange: (next: PromoContentDTO) => void;
  files: BandFileDTO[];
  /** Tour-level blobs show the "Tour title" field; the band blob does not. */
  showTitle?: boolean;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const bioRef = useRef<HTMLTextAreaElement | null>(null);
  // Native HTML5 drag & drop state: what is being dragged + the current drop
  // target (for the position indicator). Touch devices use the ↑/↓ buttons.
  // `block` = a whole content block of the editor column (blockOrder);
  // `item` = one item inside a section.
  const [drag, setDrag] = useState<{ kind: 'block'; index: number } | { kind: 'item'; sec: number; index: number } | null>(null);
  const [over, setOver] = useState<string | null>(null);

  const set = (patch: Partial<PromoContentDTO>) => onChange({ ...value, ...patch });

  const setSection = (i: number, patch: Partial<PromoContentSection>) => {
    const sections = value.sections.map((s, j) => (j === i ? { ...s, ...patch } : s));
    set({ sections });
  };

  // ---- BLOCK ORDER (spec docs/SPEC-PROMO-BLOCK-ORDER.md) ----
  // The resolved total order of blocks (blockOrder + canonical defaults).
  const blockIds = resolveBlockOrder(value.blockOrder, value.sections);
  /** Map a `section:<key>` block id back to its index in value.sections. */
  const sectionIndexOf = (id: string) => {
    const key = id.slice('section:'.length);
    return value.sections.findIndex((s) => s.key === key);
  };
  /**
   * Apply a new block order: persist `blockOrder` AND keep the sections'
   * `position` fields coherent (they remain the canonical fallback order).
   */
  const applyBlockOrder = (order: string[]) => {
    let pos = 0;
    const sections = value.sections.map((s) => ({ ...s }));
    for (const id of order) {
      if (!id.startsWith('section:')) continue;
      const i = sectionIndexOf(id);
      if (i >= 0) sections[i] = { ...sections[i], position: pos++ };
    }
    set({ blockOrder: order, sections });
  };

  const moveBlock = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= blockIds.length) return;
    const order = [...blockIds];
    [order[i], order[j]] = [order[j], order[i]];
    applyBlockOrder(order);
  };

  const dropOnBlock = (i: number) => {
    if (!drag || drag.kind !== 'block') return;
    if (drag.index === i) return;
    const order = [...blockIds];
    const [moved] = order.splice(drag.index, 1);
    order.splice(i, 0, moved);
    applyBlockOrder(order);
  };

  const addSection = () => {
    const key = `section-${Date.now().toString(36)}`;
    const position = value.sections.length;
    // A new section lands right after the LAST section block of the current
    // order (or before socials when the order has no section block) — never
    // silently appended at the very end of the page.
    const order = [...blockIds];
    let insertAt = order.lastIndexOf('socials');
    for (let k = order.length - 1; k >= 0; k--) {
      if (order[k].startsWith('section:')) { insertAt = k + 1; break; }
    }
    if (insertAt < 0) insertAt = order.length;
    order.splice(insertAt, 0, `section:${key}`);
    set({
      sections: [...value.sections, { key, title: '', fileIds: [], items: [], position }],
      blockOrder: order,
    });
  };

  const removeSection = (i: number) => {
    const key = value.sections[i]?.key;
    const sections = value.sections.filter((_, j) => j !== i).map((s, k) => ({ ...s, position: k }));
    if (key) {
      // The block id leaves the order with the section (stale ids are also
      // tolerated server-side — this keeps the editor state immediately clean).
      set({ sections, blockOrder: blockIds.filter((id) => id !== `section:${key}`) });
    } else {
      set({ sections });
    }
  };

  // HIDDEN (spec docs/SPEC-PROMO-EDITOR-V3.md, 2026-10-20): the 👁 toggle adds
  // / removes the additive `hidden` key (absent = visible, strict backcompat).
  // A hidden section stays in the editor (grayed), still draggable, still in
  // the blob — the public page simply skips it; the ZIP pack is untouched.
  const hiddenCount = value.sections.filter((s) => s.hidden === true).length;
  const toggleSectionHidden = (i: number) => {
    const sec = value.sections[i];
    if (!sec) return;
    if (sec.hidden === true) {
      // Back to visible = remove the key entirely (never store hidden:false).
      const sections = value.sections.map((s, j) => {
        if (j !== i) return s;
        const { hidden: _drop, ...rest } = s;
        return rest as PromoContentSection;
      });
      set({ sections });
    } else {
      setSection(i, { hidden: true });
    }
  };
  const showAllSections = () => {
    set({ sections: value.sections.map((s) => {
      if (s.hidden !== true) return s;
      const { hidden: _drop, ...rest } = s;
      return rest as PromoContentSection;
    }) });
  };

  // ---- items (mixed files / links / videos) ----
  const fileItemsOf = (items: PromoContentItem[]) =>
    items.filter((it): it is Extract<PromoContentItem, { type: 'file' }> => it.type === 'file').map((it) => it.fileId);

  const setItems = (i: number, items: PromoContentItem[]) =>
    setSection(i, { items, fileIds: fileItemsOf(items) });

  const moveItem = (i: number, j: number, dir: -1 | 1) => {
    const sec = value.sections[i];
    if (!sec) return;
    const k = j + dir;
    if (k < 0 || k >= sec.items.length) return;
    const items = [...sec.items];
    [items[j], items[k]] = [items[k], items[j]];
    setItems(i, items);
  };

  const removeItem = (i: number, j: number) => {
    const sec = value.sections[i];
    if (!sec) return;
    setItems(i, sec.items.filter((_, k) => k !== j));
  };

  const addLinkItem = (i: number) => {
    const sec = value.sections[i];
    if (!sec) return;
    setItems(i, [...sec.items, { type: 'link', label: '', url: '' }]);
  };

  const addVideoItem = (i: number) => {
    const sec = value.sections[i];
    if (!sec) return;
    setItems(i, [...sec.items, { type: 'video', url: '' }]);
  };

  const fileById = new Map(files.map((f) => [f.id, f]));
  const usedIds = new Set(value.sections.flatMap((s) => s.fileIds));
  const missingIds = (sec: PromoContentSection) =>
    sec.fileIds.filter((id) => !fileById.has(id));

  const addFileToSection = (i: number, id: number) => {
    const sec = value.sections[i];
    if (!sec || sec.fileIds.includes(id)) return;
    setItems(i, [...sec.items, { type: 'file', fileId: id }]);
  };

  // ---- drag & drop (native HTML5; touch keeps the ↑/↓ buttons) ----
  const dragData = (d: NonNullable<typeof drag>) => JSON.stringify(d);

  const dropOnSectionItems = (i: number) => {
    if (!drag) return;
    if (drag.kind === 'item') {
      dropOnItem(i, value.sections[i]?.items.length ?? 0);
    }
  };

  const dropOnItem = (i: number, j: number) => {
    if (!drag || drag.kind !== 'item') return;
    if (drag.sec === i && (drag.index === j || drag.index + 1 === j)) return;
    const from = value.sections[drag.sec];
    const to = value.sections[i];
    if (!from || !to) return;
    const items = [...from.items];
    const [moved] = items.splice(drag.index, 1);
    if (!moved) return;
    if (drag.sec === i) {
      const target = drag.index < j ? j - 1 : j;
      items.splice(target, 0, moved);
      setItems(i, items);
    } else {
      const destItems = [...to.items];
      destItems.splice(j, 0, moved);
      const sections = [...value.sections];
      sections[drag.sec] = { ...from, items, fileIds: fileItemsOf(items) };
      sections[i] = { ...to, items: destItems, fileIds: fileItemsOf(destItems) };
      set({ sections });
    }
  };

  // ---- bio formatting bar (markdown behind the buttons) ----
  /** Wrap (or insert) markdown around the bio textarea selection. */
  const wrapBio = (before: string, after: string, placeholder: string) => {
    const el = bioRef.current;
    if (!el || disabled) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const val = value.bio || '';
    const sel = val.slice(start, end) || placeholder;
    const next = val.slice(0, start) + before + sel + after + val.slice(end);
    set({ bio: next });
    // Keep the user's place: reselect the wrapped text (they can overtype it).
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + before.length, start + before.length + sel.length);
    });
  };

  /** Prefix every selected line with "- " (a markdown list). */
  const bioList = () => {
    const el = bioRef.current;
    if (!el || disabled) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const val = value.bio || '';
    const lineStart = val.lastIndexOf('\n', Math.max(0, start - 1)) + 1;
    const sel = val.slice(lineStart, end) || t('promoContent.mdListText');
    const prefixed = sel.split('\n').map((l) => (l.trim() ? `- ${l.replace(/^- /, '')}` : l)).join('\n');
    const next = val.slice(0, lineStart) + prefixed + val.slice(end);
    set({ bio: next });
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(lineStart, lineStart + prefixed.length);
    });
  };

  // ---- hero image (option B) — FIXED at the top, never draggable ----
  const heroImages = files.filter((f) => String(f.mime || '').startsWith('image/'));
  const heroMode: 'auto' | 'none' | 'pick' =
    value.heroImageFileId === 0 ? 'none' : value.heroImageFileId ? 'pick' : 'auto';
  const setHeroMode = (mode: 'auto' | 'none' | 'pick', id?: number) => {
    if (mode === 'auto') {
      // Absent key = exact legacy blob (strict backcompat) — never store a 0.
      const { heroImageFileId: _drop, ...rest } = value;
      onChange(rest as PromoContentDTO);
    } else if (mode === 'none') {
      set({ heroImageFileId: 0 });
    } else if (id) {
      set({ heroImageFileId: id });
    }
  };

  const mdBtn = (label: React.ReactNode, title: string, onClick: () => void) => (
    <button
      type="button"
      className="btn btn-small"
      title={title}
      aria-label={title}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()} // keep the textarea selection
      onClick={onClick}
    >
      {label}
    </button>
  );

  // ---- content blocks (rendered in the resolved blockOrder) ----
  const blockLabel = (id: string) => {
    if (id === 'intro') return t('promoContent.intro');
    if (id === 'bio') return t('promoContent.bio');
    if (id === 'quotes') return t('promoContent.quotes');
    if (id === 'socials') return t('promoContent.socialsLabel');
    if (id === 'contact') return t('promoContent.contact');
    return '';
  };

  const introBlock = (
    <div className="form-field" style={{ marginBottom: 10 }}>
      <AutoTextarea
        className="form-input"
        rows={2}
        value={value.intro || ''}
        placeholder={t('promoContent.introPh')}
        disabled={disabled}
        onChange={(e) => set({ intro: e.target.value })}
      />
    </div>
  );

  const bioBlock = (
    <div className="form-field" style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 4 }} data-promo-bio-toolbar>
        {mdBtn(<strong>B</strong>, t('promoContent.mdBold'), () => wrapBio('**', '**', t('promoContent.mdBoldText')))}
        {mdBtn(<em>I</em>, t('promoContent.mdItalic'), () => wrapBio('*', '*', t('promoContent.mdItalicText')))}
        {mdBtn('🔗', t('promoContent.mdLink'), () => wrapBio('[', '](https://)', t('promoContent.mdLinkText')))}
        {mdBtn('≔', t('promoContent.mdList'), bioList)}
      </div>
      <AutoTextarea
        ref={bioRef}
        className="form-input"
        rows={6}
        value={value.bio || ''}
        placeholder={t('promoContent.bioPh')}
        disabled={disabled}
        data-promo-bio-input
        onChange={(e) => set({ bio: e.target.value })}
      />
    </div>
  );

  const quotesBlock = (
    <div className="form-field" style={{ marginBottom: 10 }} data-promo-quotes>
      {value.quotes.map((q, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
          <AutoTextarea
            className="form-input"
            value={q.text}
            placeholder={t('promoContent.quoteTextPh')}
            disabled={disabled}
            rows={2}
            style={{ flex: 2, minWidth: 200 }}
            onChange={(e) => {
              const quotes = value.quotes.map((x, j) => (j === i ? { ...x, text: e.target.value } : x));
              set({ quotes });
            }}
          />
          <input
            className="form-input"
            value={q.source || ''}
            placeholder={t('promoContent.quoteSourcePh')}
            disabled={disabled}
            style={{ flex: 1, minWidth: 140 }}
            onChange={(e) => {
              const quotes = value.quotes.map((x, j) => (j === i ? { ...x, source: e.target.value } : x));
              set({ quotes });
            }}
          />
          <button
            type="button"
            className="btn btn-small btn-danger"
            aria-label={t('promoContent.removeQuote')}
            disabled={disabled}
            onClick={() => set({ quotes: value.quotes.filter((_, j) => j !== i) })}
          >
            ✕
          </button>
        </div>
      ))}
      <button
        type="button"
        className="btn btn-secondary btn-small"
        disabled={disabled || value.quotes.length >= 30}
        onClick={() => set({ quotes: [...value.quotes, { text: '', source: '' }] })}
      >
        + {t('promoContent.addQuote')}
      </button>
    </div>
  );

  const socialsBlock = (
    // SOCIALS (Sandro v2, 2026-10-19): one http(s) URL per network; the emoji
    // icons are EDITOR-ONLY (the public page renders inline SVGs). Empty = no
    // icon. The server whitelists the keys and rejects any non-http(s) URL
    // with a 400 surfaced by the parent's Save.
    <div className="form-field" style={{ marginBottom: 10 }} data-promo-socials-editor>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 8 }}>
        {(['instagram', 'tiktok', 'spotify', 'youtube', 'website'] as const).map((key) => (
          <label key={key} style={{ display: 'flex', gap: 6, alignItems: 'center', minWidth: 0 }}>
            <span aria-hidden="true" style={{ flexShrink: 0 }}>
              {key === 'instagram' ? '📷' : key === 'tiktok' ? '🎵' : key === 'spotify' ? '🟢' : key === 'youtube' ? '▶' : '🌐'}
            </span>
            <input
              className="form-input"
              value={value.socials?.[key] || ''}
              placeholder={t(`promoContent.socialPh_${key}`)}
              disabled={disabled}
              inputMode="url"
              style={{ minWidth: 0 }}
              data-promo-social-input={key}
              onChange={(e) => {
                const next = { ...(value.socials || {}), [key]: e.target.value };
                if (!Object.values(next).some((v) => String(v || '').trim())) delete next[key];
                set({ socials: next as PromoContentDTO['socials'] });
              }}
            />
          </label>
        ))}
      </div>
      <p className="text-dim" style={{ margin: '4px 0 0', fontSize: 'var(--font-xs)' }}>
        {t('promoContent.socialsHint')}
      </p>
    </div>
  );

  const contactBlock = (
    <div className="form-field" style={{ marginBottom: 10 }}>
      <AutoTextarea
        className="form-input"
        rows={2}
        value={value.contact || ''}
        placeholder={t('promoContent.contactPh')}
        disabled={disabled}
        onChange={(e) => set({ contact: e.target.value })}
      />
    </div>
  );

  /** One SECTION as a movable block (its items keep their own drag & drop). */
  const sectionBlock = (i: number) => {
    const sec = value.sections[i];
    if (!sec) return null;
    const missing = missingIds(sec);
    return (
      <div
        style={{
          // Hidden section: grayed but fully interactive (drag included).
          opacity: sec.hidden === true ? 0.55 : undefined,
          borderStyle: sec.hidden === true ? 'dashed' : undefined,
        }}
        data-promo-section={sec.key || i}
        data-promo-section-hidden={sec.hidden === true ? 'true' : undefined}
      >
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
          <input
            className="form-input"
            value={sec.title}
            placeholder={t('promoContent.sectionTitlePh')}
            disabled={disabled}
            style={{ flex: 1 }}
            onChange={(e) => setSection(i, { title: e.target.value })}
          />
          <button
            type="button"
            className={`btn btn-small${sec.hidden === true ? ' btn-primary' : ''}`}
            title={sec.hidden === true ? t('promoContent.showSection') : t('promoContent.hideSection')}
            aria-label={sec.hidden === true ? t('promoContent.showSection') : t('promoContent.hideSection')}
            aria-pressed={sec.hidden === true}
            disabled={disabled}
            onClick={() => toggleSectionHidden(i)}
            data-promo-toggle-hidden={sec.hidden === true ? 'on' : 'off'}
          >
            {sec.hidden === true ? '🚫' : '👁'}
          </button>
          <button type="button" className="btn btn-small btn-danger" title={t('promoContent.removeSection')} aria-label={t('promoContent.removeSection')} disabled={disabled} onClick={() => removeSection(i)}>✕</button>
        </div>
        {sec.items.map((it, j) => {
          const itemOver = over === `item-${i}-${j}`;
          return (
            <div
              key={j}
              style={{
                display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 6,
                outline: itemOver ? '2px dashed var(--accent, #e0a44c)' : undefined,
                outlineOffset: 2,
                borderRadius: 4,
              }}
              data-promo-item={it.type}
              onDragOver={(e) => { if (drag && drag.kind === 'item') { e.preventDefault(); e.stopPropagation(); setOver(`item-${i}-${j}`); } }}
              onDragLeave={() => setOver((o) => (o === `item-${i}-${j}` ? null : o))}
              onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setOver(null); dropOnItem(i, j); }}
            >
              <span
                draggable={!disabled}
                onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', dragData({ kind: 'item', sec: i, index: j })); setDrag({ kind: 'item', sec: i, index: j }); }}
                onDragEnd={() => { setDrag(null); setOver(null); }}
                title={t('promoContent.dragHandle')}
                aria-label={t('promoContent.dragHandle')}
                style={{ cursor: disabled ? 'default' : 'grab', userSelect: 'none', padding: '2px 2px 0' }}
                data-promo-drag-handle="item"
              >
                ⠿
              </span>
              {it.type === 'file' ? (
                <span className="rc-chip" style={{ flex: 1, display: 'inline-flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                  {fileById.get(it.fileId)?.filename || `#${it.fileId} — ${t('promoContent.missingFile')}`}
                </span>
              ) : it.type === 'link' ? (
                <span style={{ flex: 1, display: 'flex', gap: 6, flexDirection: 'column' }} data-promo-item-link>
                  <input
                    className="form-input"
                    value={it.label}
                    placeholder={t('promoContent.linkLabelPh')}
                    disabled={disabled}
                    maxLength={120}
                    onChange={(e) => {
                      const items = sec.items.map((x, k) => (k === j && x.type === 'link' ? { ...x, label: e.target.value } : x));
                      setItems(i, items);
                    }}
                  />
                  <input
                    className="form-input"
                    value={it.url}
                    placeholder={t('promoContent.linkUrlPh')}
                    disabled={disabled}
                    inputMode="url"
                    onChange={(e) => {
                      const items = sec.items.map((x, k) => (k === j && x.type === 'link' ? { ...x, url: e.target.value } : x));
                      setItems(i, items);
                    }}
                  />
                </span>
              ) : (
                <span style={{ flex: 1, display: 'flex', gap: 6, flexDirection: 'column' }} data-promo-item-video>
                  <input
                    className="form-input"
                    value={it.url}
                    placeholder={t('promoContent.videoUrlPh')}
                    disabled={disabled}
                    inputMode="url"
                    onChange={(e) => {
                      const items = sec.items.map((x, k) => (k === j && x.type === 'video' ? { ...x, url: e.target.value } : x));
                      setItems(i, items);
                    }}
                  />
                  {it.url && (
                    <span className="text-dim" style={{ fontSize: 'var(--font-xs)' }}>
                      {t('promoContent.videoNormalized')}
                    </span>
                  )}
                </span>
              )}
              <span style={{ display: 'flex', gap: 4 }}>
                <button type="button" className="btn btn-small" title={t('promoContent.moveUp')} aria-label={t('promoContent.moveUp')} disabled={disabled || j === 0} onClick={() => moveItem(i, j, -1)}>↑</button>
                <button type="button" className="btn btn-small" title={t('promoContent.moveDown')} aria-label={t('promoContent.moveDown')} disabled={disabled || j === sec.items.length - 1} onClick={() => moveItem(i, j, 1)}>↓</button>
                <button type="button" className="btn btn-small btn-danger" title={t('promoContent.removeItem')} aria-label={t('promoContent.removeItem')} disabled={disabled} onClick={() => removeItem(i, j)}>✕</button>
              </span>
            </div>
          );
        })}
        {missing.length > 0 && (
          <div style={{ marginBottom: 6 }}>
            {missing.map((id) => (
              <span key={id} className="rc-chip" style={{ marginRight: 6, marginBottom: 4, display: 'inline-flex', alignItems: 'center', gap: 4, opacity: 0.6 }} title={t('promoContent.missingFile')}>
                #{id} — {t('promoContent.missingFile')}
                <button
                  type="button"
                  aria-label={t('promoContent.removeMissing')}
                  disabled={disabled}
                  style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
                  onClick={() => removeItem(i, sec.items.findIndex((x) => x.type === 'file' && x.fileId === id))}
                >
                  ✕
                </button>
              </span>
            ))}
          </div>
        )}
        <div style={{ marginTop: 6, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <select
            className="form-input"
            style={{ maxWidth: 360, flex: '1 1 240px' }}
            value=""
            disabled={disabled || files.length === 0}
            onChange={(e) => { const id = Number(e.target.value); if (id) addFileToSection(i, id); }}
            aria-label={t('promoContent.addFile')}
          >
            <option value="">{t('promoContent.addFile')}</option>
            {files
              .filter((f) => !sec.fileIds.includes(f.id))
              .map((f) => (
                <option key={f.id} value={f.id}>
                  {f.filename}{usedIds.has(f.id) ? ` — ${t('promoContent.alsoInOtherSection')}` : ''}
                </option>
              ))}
          </select>
          <button type="button" className="btn btn-secondary btn-small" disabled={disabled} onClick={() => addLinkItem(i)}>
            + {t('promoContent.addLink')}
          </button>
          <button type="button" className="btn btn-secondary btn-small" disabled={disabled} onClick={() => addVideoItem(i)}>
            + {t('promoContent.addVideo')}
          </button>
          {files.length === 0 && (
            <span className="text-dim" style={{ fontSize: 'var(--font-xs)' }}>
              {t('promoContent.noFilesHint')}
            </span>
          )}
        </div>
      </div>
    );
  };

  /** Render the content of one block id. */
  const blockContent = (id: string) => {
    if (id === 'intro') return introBlock;
    if (id === 'bio') return bioBlock;
    if (id === 'quotes') return quotesBlock;
    if (id === 'socials') return socialsBlock;
    if (id === 'contact') return contactBlock;
    if (id.startsWith('section:')) return sectionBlock(sectionIndexOf(id));
    return null;
  };

  return (
    <div data-promo-content-editor>
      {showTitle && (
        <div className="form-field" style={{ marginBottom: 10 }}>
          <label>{t('promoContent.tourTitle')}</label>
          <input
            className="form-input"
            value={value.tourTitle || ''}
            placeholder={t('promoContent.tourTitlePh')}
            disabled={disabled}
            onChange={(e) => set({ tourTitle: e.target.value })}
          />
        </div>
      )}

      {/* HERO IMAGE — fixed banner at the top of the editor, OUTSIDE the
          draggable block list (the hero is always first on the public page). */}
      <div className="form-field" style={{ marginBottom: 10 }} data-promo-hero-picker>
        <label>{t('promoContent.heroImage')}</label>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          <label style={{ display: 'inline-flex', gap: 5, alignItems: 'center' }}>
            <input
              type="radio"
              name="promo-hero-mode"
              checked={heroMode === 'auto'}
              disabled={disabled}
              onChange={() => setHeroMode('auto')}
            />
            {t('promoContent.heroAuto')}
          </label>
          <label style={{ display: 'inline-flex', gap: 5, alignItems: 'center' }}>
            <input
              type="radio"
              name="promo-hero-mode"
              checked={heroMode === 'none'}
              disabled={disabled}
              onChange={() => setHeroMode('none')}
            />
            {t('promoContent.heroNone')}
          </label>
          <label style={{ display: 'inline-flex', gap: 5, alignItems: 'center' }}>
            <input
              type="radio"
              name="promo-hero-mode"
              checked={heroMode === 'pick'}
              disabled={disabled}
              onChange={() => setHeroMode('pick', heroImages[0]?.id)}
            />
            {t('promoContent.heroPick')}
          </label>
          <select
            className="form-input"
            style={{ maxWidth: 320 }}
            value={heroMode === 'pick' && value.heroImageFileId ? String(value.heroImageFileId) : ''}
            disabled={disabled || heroMode !== 'pick' || heroImages.length === 0}
            onChange={(e) => { const id = Number(e.target.value); if (id) setHeroMode('pick', id); }}
            aria-label={t('promoContent.heroPick')}
          >
            <option value="">{heroImages.length ? t('promoContent.heroPickPh') : t('promoContent.heroNoImages')}</option>
            {heroImages.map((f) => (
              <option key={f.id} value={f.id}>{f.filename}</option>
            ))}
          </select>
        </div>
        <p className="text-dim" style={{ margin: '4px 0 0', fontSize: 'var(--font-xs)' }}>
          {t('promoContent.heroHint')}
        </p>
      </div>

      {/* MOVABLE BLOCK LIST (blockOrder) — every content block can be dragged
          (⠿ handle, same as the items) or moved with ↑/↓. */}
      <div className="form-field" style={{ marginBottom: 10 }} data-promo-blocks>
        <label>{t('promoContent.blocks')}</label>
        <p className="text-dim" style={{ margin: '0 0 10px', fontSize: 'var(--font-xs)' }}>
          {t('promoContent.blocksHint')}
        </p>
        {/* Hidden counter + "show all" (confort v3): appears only when
            sections are hidden; ≥ 2 hidden enables the one-click reset. */}
        {hiddenCount > 0 && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }} data-promo-hidden-bar>
            <span className="rc-chip" data-promo-hidden-count={hiddenCount}>
              👁 {t('promoContent.hiddenCount', { count: hiddenCount })}
            </span>
            {hiddenCount >= 2 && (
              <button
                type="button"
                className="btn btn-small"
                disabled={disabled}
                onClick={showAllSections}
                data-promo-show-all
              >
                {t('promoContent.showAllSections')}
              </button>
            )}
          </div>
        )}
        {blockIds.map((id, bi) => {
          const blockOver = over === `block-${bi}`;
          const label = blockLabel(id);
          return (
            <div
              key={id}
              style={{
                border: `1px solid ${blockOver ? 'var(--accent, #e0a44c)' : 'var(--border)'}`,
                borderRadius: 8,
                padding: 10,
                marginBottom: 10,
                background: blockOver ? 'var(--hover, rgba(255,255,255,.04))' : undefined,
              }}
              data-promo-block={id}
              onDragOver={(e) => {
                if (!drag) return;
                // Blocks reorder between themselves; an ITEM dragged over a
                // section block (between its rows) targets that section too.
                if (drag.kind === 'block' || (drag.kind === 'item' && id.startsWith('section:'))) {
                  e.preventDefault();
                  setOver(`block-${bi}`);
                }
              }}
              onDragLeave={() => setOver((o) => (o === `block-${bi}` ? null : o))}
              onDrop={(e) => {
                e.preventDefault();
                const wasItem = drag?.kind === 'item';
                setOver(null);
                if (wasItem && id.startsWith('section:')) {
                  // An item dragged over a section BLOCK lands at the end of
                  // that section (cross-section moves keep working).
                  dropOnSectionItems(sectionIndexOf(id));
                } else {
                  dropOnBlock(bi);
                }
              }}
            >
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                <span
                  draggable={!disabled}
                  onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', dragData({ kind: 'block', index: bi })); setDrag({ kind: 'block', index: bi }); }}
                  onDragEnd={() => { setDrag(null); setOver(null); }}
                  title={t('promoContent.dragHandle')}
                  aria-label={t('promoContent.dragHandle')}
                  style={{ cursor: disabled ? 'default' : 'grab', userSelect: 'none', padding: '0 2px' }}
                  data-promo-drag-handle="block"
                >
                  ⠿
                </span>
                <span style={{ flex: 1, fontWeight: 600, fontSize: '0.9em' }}>{label}</span>
                <button type="button" className="btn btn-small" title={t('promoContent.moveUp')} aria-label={t('promoContent.moveUp')} disabled={disabled || bi === 0} onClick={() => moveBlock(bi, -1)}>↑</button>
                <button type="button" className="btn btn-small" title={t('promoContent.moveDown')} aria-label={t('promoContent.moveDown')} disabled={disabled || bi === blockIds.length - 1} onClick={() => moveBlock(bi, 1)}>↓</button>
              </div>
              {blockContent(id)}
            </div>
          );
        })}
        <button type="button" className="btn btn-secondary btn-small" disabled={disabled || value.sections.length >= 20} onClick={addSection}>
          + {t('promoContent.addSection')}
        </button>
      </div>
    </div>
  );
}
